/**
 * S2PUploadModal.js
 *
 * Modal for uploading S2P (Touchstone) files for frequency-dependent component data
 * Allows drag-drop or file picker, shows upload progress, and validates S2P format
 */

import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import THEME from '../../styles/theme';

// API base URL - uses environment variable or defaults to localhost for development
const API_BASE_URL = '';

const S2PUploadModal = ({ isOpen, onClose, componentLibraryId, componentName, onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleFileSelect = (file) => {
    setError(null);
    setUploadResult(null);

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.s2p')) {
      setError('Invalid file type. Please select a .s2p file.');
      return;
    }

    // Validate file size (10 MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10 MB.');
      return;
    }

    setSelectedFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('s2pFile', selectedFile);

      const token = localStorage.getItem('gchain_token');
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const response = await fetch(`${API_BASE_URL}/api/components/${componentLibraryId}/s2p`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Server error response:', errorData);
        const errorMsg = errorData.details
          ? `${errorData.error}: ${errorData.details}`
          : errorData.error || `Upload failed with status ${response.status}`;
        throw new Error(errorMsg);
      }

      const result = await response.json();
      console.log('Upload successful:', result);
      setUploadProgress(100);
      setUploadResult(result);

      // Call success callback after a short delay to show success message
      setTimeout(() => {
        onUploadSuccess(result);
        handleClose();
      }, 1500);

    } catch (err) {
      console.error('S2P upload error:', err);
      console.error('Component Library ID:', componentLibraryId);
      console.error('File:', selectedFile?.name);
      setError(err.message);
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setError(null);
    setUploadResult(null);
    setUploadProgress(0);
    setUploading(false);
    onClose();
  };

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Upload S2P File</ModalTitle>
          <CloseButton onClick={handleClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalBody>
          <ComponentInfo>
            <InfoLabel>Component:</InfoLabel>
            <InfoValue>{componentName}</InfoValue>
          </ComponentInfo>

          <DropZone
            $isDragging={isDragging}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            {selectedFile ? (
              <>
                <FileIcon>📄</FileIcon>
                <FileName>{selectedFile.name}</FileName>
                <FileSize>
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </FileSize>
              </>
            ) : (
              <>
                <UploadIcon>📊</UploadIcon>
                <DropText>
                  Drag and drop .s2p file here
                  <br />or click to browse
                </DropText>
                <DropHint>Maximum file size: 10 MB</DropHint>
              </>
            )}
          </DropZone>

          <input
            ref={fileInputRef}
            type="file"
            accept=".s2p"
            style={{ display: 'none' }}
            onChange={handleFileInputChange}
          />

          {uploading && (
            <ProgressContainer>
              <ProgressBar>
                <ProgressFill $progress={uploadProgress} />
              </ProgressBar>
              <ProgressText>{uploadProgress}%</ProgressText>
            </ProgressContainer>
          )}

          {error && (
            <ErrorMessage>
              ⚠️ {error}
            </ErrorMessage>
          )}

          {uploadResult && (
            <SuccessMessage>
              ✅ S2P file uploaded successfully!
              <ResultDetails>
                Frequency range: {uploadResult.freqMin?.toFixed(0)} - {uploadResult.freqMax?.toFixed(0)} MHz
                <br />
                Data points: {uploadResult.numPoints}
              </ResultDetails>
            </SuccessMessage>
          )}

          <InfoSection>
            <InfoTitle>About S2P Files</InfoTitle>
            <InfoText>
              S2P (Touchstone) files contain measured S-parameter data for RF components.
              This data will be used for frequency-dependent gain calculations.
            </InfoText>
            <InfoText>
              • Supported formats: DB (dB-angle), MA (mag-angle), RI (real-imag)
              <br />
              • Frequency units: Hz, kHz, MHz, GHz
              <br />
              • Used for: Gain (S21 magnitude)
            </InfoText>
          </InfoSection>
        </ModalBody>

        <ModalFooter>
          <CancelButton onClick={handleClose} disabled={uploading}>
            Cancel
          </CancelButton>
          <UploadButton
            onClick={handleUpload}
            disabled={!selectedFile || uploading || uploadResult !== null}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </UploadButton>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: ${THEME.background};
  border: 1px solid ${THEME.border};
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid ${THEME.border};
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  color: ${THEME.textPrimary};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${THEME.textSecondary};
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all ${THEME.transitionFast};

  &:hover {
    background: ${THEME.hoverBackground};
    color: ${THEME.textPrimary};
  }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const ComponentInfo = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  padding: 12px;
  background: ${THEME.cardBackground};
  border-radius: 6px;
  border: 1px solid ${THEME.border};
`;

const InfoLabel = styled.span`
  color: ${THEME.textSecondary};
  font-size: 14px;
`;

const InfoValue = styled.span`
  color: ${THEME.textPrimary};
  font-size: 14px;
  font-weight: 600;
`;

const DropZone = styled.div`
  border: 2px dashed ${props => props.$isDragging ? THEME.accent : THEME.border};
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all ${THEME.transitionFast};
  background: ${props => props.$isDragging ? 'rgba(59, 130, 246, 0.1)' : THEME.cardBackground};
  margin-bottom: 16px;

  &:hover {
    border-color: ${THEME.accent};
    background: rgba(59, 130, 246, 0.05);
  }
`;

const UploadIcon = styled.div`
  font-size: 48px;
  margin-bottom: 12px;
`;

const FileIcon = styled.div`
  font-size: 48px;
  margin-bottom: 12px;
`;

const DropText = styled.div`
  color: ${THEME.textPrimary};
  font-size: 16px;
  margin-bottom: 8px;
  line-height: 1.5;
`;

const DropHint = styled.div`
  color: ${THEME.textSecondary};
  font-size: 12px;
`;

const FileName = styled.div`
  color: ${THEME.textPrimary};
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const FileSize = styled.div`
  color: ${THEME.textSecondary};
  font-size: 14px;
`;

const ProgressContainer = styled.div`
  margin-bottom: 16px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${THEME.cardBackground};
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${THEME.accent};
  width: ${props => props.$progress}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  text-align: center;
  color: ${THEME.textSecondary};
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 6px;
  color: #ef4444;
  font-size: 14px;
  margin-bottom: 16px;
`;

const SuccessMessage = styled.div`
  padding: 12px;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 6px;
  color: #22c55e;
  font-size: 14px;
  margin-bottom: 16px;
`;

const ResultDetails = styled.div`
  margin-top: 8px;
  font-size: 12px;
  opacity: 0.9;
`;

const InfoSection = styled.div`
  padding: 16px;
  background: ${THEME.cardBackground};
  border-radius: 6px;
  border: 1px solid ${THEME.border};
`;

const InfoTitle = styled.div`
  color: ${THEME.textPrimary};
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const InfoText = styled.div`
  color: ${THEME.textSecondary};
  font-size: 12px;
  line-height: 1.6;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid ${THEME.border};
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all ${THEME.transitionFast};
  border: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: ${THEME.cardBackground};
  color: ${THEME.textSecondary};
  border: 1px solid ${THEME.border};

  &:hover:not(:disabled) {
    background: ${THEME.hoverBackground};
    color: ${THEME.textPrimary};
  }
`;

const UploadButton = styled(Button)`
  background: ${THEME.accent};
  color: white;

  &:hover:not(:disabled) {
    background: #2563eb;
  }
`;

export default S2PUploadModal;
